/**
 * Serviço de API - Configurações de SLA
 * 
 * Este módulo gerencia todas as operações relacionadas às Configurações de SLA:
 * - Operações CRUD completas (criar, listar, obter, atualizar, deletar)
 * - Verificação de SLA de OS
 * - Contagem de OS estouradas
 * 
 * @author Equipe GestaoeCRM
 * @date 2025-11-05
 * @version 1.0.0
 */

import api from './api';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

export type Priority = 'BAIXA' | 'MEDIA' | 'ALTA' | 'CRITICA';
export type SLATimeUnit = 'HOURS' | 'DAYS';

export interface SLAConfiguration {
  id: string;
  tenantId: string;
  priority: Priority;
  timeValue: number;
  timeUnit: SLATimeUnit;
  createdAt: string;
  updatedAt: string;
}

export interface SLACheckResult {
  isOverdue: boolean;
  deadline: string;
  lastActivityDate: string;
  timeRemaining?: number;
  timeOverdue?: number;
  slaConfig?: {
    timeValue: number;
    timeUnit: SLATimeUnit;
  };
}

// ============================================================================
// API DE SLA
// ============================================================================

export const slaApi = {
  /**
   * Lista todas as configurações de SLA do tenant
   */
  getAll: (): Promise<{ success: boolean; data: SLAConfiguration[]; count: number }> =>
    api.get('/sla'),

  /**
   * Busca uma configuração de SLA por ID
   */
  getById: (id: string): Promise<{ success: boolean; data: SLAConfiguration }> =>
    api.get(`/sla/${id}`),

  /**
   * Busca configuração de SLA por prioridade
   */
  getByPriority: (priority: Priority): Promise<{ success: boolean; data: SLAConfiguration }> =>
    api.get(`/sla/priority/${priority}`),

  /**
   * Cria uma nova configuração de SLA
   */
  create: (data: {
    priority: Priority;
    timeValue: number;
    timeUnit: SLATimeUnit;
  }): Promise<{ success: boolean; message: string; data: SLAConfiguration }> =>
    api.post('/sla', data),

  /**
   * Atualiza uma configuração de SLA por ID
   */
  update: (
    id: string,
    data: {
      timeValue?: number;
      timeUnit?: SLATimeUnit;
    }
  ): Promise<{ success: boolean; message: string; data: SLAConfiguration }> =>
    api.put(`/sla/${id}`, data),

  /**
   * Cria ou atualiza configuração de SLA por prioridade
   */
  upsertByPriority: (
    priority: Priority,
    data: {
      timeValue: number;
      timeUnit: SLATimeUnit;
    }
  ): Promise<{ success: boolean; message: string; data: SLAConfiguration }> =>
    api.put(`/sla/priority/${priority}`, data),

  /**
   * Deleta uma configuração de SLA
   */
  delete: (id: string): Promise<{ success: boolean; message: string }> =>
    api.delete(`/sla/${id}`),

  /**
   * Verifica SLA de uma OS específica
   */
  checkOS: (osId: string): Promise<{ success: boolean; data: SLACheckResult }> =>
    api.get(`/sla/check/os/${osId}`),

  /**
   * Conta OS estouradas do tenant
   */
  getOverdueCount: async (projectId?: string): Promise<{ success: boolean; data: { count: number; overdueOSIds: string[] } }> => {
    // console.log('[SLA API] Chamando endpoint /sla/check/overdue/count com params:', projectId ? { projectId } : {});
    try {
      const response = await api.get('/sla/check/overdue/count', { params: projectId ? { projectId } : {} });
      // console.log('[SLA API] Resposta completa:', response);
      // console.log('[SLA API] Response.data:', response.data);
      return response.data;
    } catch (error: any) {
      // console.error('[SLA API] Erro na requisição:', error);
      // console.error('[SLA API] Erro.response:', error?.response);
      // console.error('[SLA API] Erro.request:', error?.request);
      throw error;
    }
  },

  /**
   * Lista todas as OS estouradas do tenant
   */
  getOverdueOS: (projectId?: string): Promise<{ success: boolean; data: string[] }> =>
    api.get('/sla/check/overdue', { params: projectId ? { projectId } : {} }),
};

